import React, { useState } from 'react';
import { useSidebar } from '../contexts/SidebarContext.jsx';

/**
 * Sidebar Preferences Component
 * Allows users to control sidebar behavior and caching
 */
const SidebarPreferences = ({ className = '' }) => {
    const {
        userPreferences,
        updateUserPreferences,
        getCacheStats,
        clearCache,
        invalidateCache
    } = useSidebar();
    
    const [isOpen, setIsOpen] = useState(false);
    const [cacheStats, setCacheStats] = useState(null);
    
    // Load cache stats when opened
    const handleToggle = () => {
        if (!isOpen) {
            setCacheStats(getCacheStats());
        }
        setIsOpen(!isOpen);
    };
    
    const handlePreferenceChange = (key, value) => {
        updateUserPreferences({ [key]: value });
    };
    
    const handleClearCache = () => {
        clearCache();
        setCacheStats(getCacheStats());
        // Show toast notification
        showToast('Cache cleared successfully', 'success');
    };
    
    const handleInvalidateCache = (pattern) => {
        invalidateCache(pattern);
        setCacheStats(getCacheStats());
        showToast(`Cache entries matching "${pattern}" cleared`, 'success');
    };
    
    // Simple toast notification
    const showToast = (message, type = 'info') => {
        const toast = document.createElement('div');
        toast.textContent = message;
        toast.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: ${type === 'success' ? '#22c55e' : type === 'error' ? '#ef4444' : '#3b82f6'};
            color: white;
            padding: 12px 16px;
            border-radius: 8px;
            z-index: 10000;
            font-size: 14px;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        `;
        document.body.appendChild(toast);
        setTimeout(() => {
            if (document.body.contains(toast)) {
                document.body.removeChild(toast);
            }
        }, 3000);
    };
    
    if (!isOpen) {
        return (
            <button
                onClick={handleToggle}
                className={`sidebar-preferences-toggle ${className}`}
                title="Sidebar Preferences"
                style={{
                    position: 'fixed',
                    bottom: '20px',
                    right: '20px',
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: 'rgba(15, 15, 17, 0.9)',
                    border: '1px solid rgba(75, 85, 99, 0.3)',
                    color: 'rgba(255, 255, 255, 0.7)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 100000,
                    transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                    e.target.style.background = 'rgba(15, 15, 17, 1)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                }}
                onMouseLeave={(e) => {
                    e.target.style.background = 'rgba(15, 15, 17, 0.9)';
                    e.target.style.color = 'rgba(255, 255, 255, 0.7)';
                }}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="3"/>
                    <path d="M12 1v6m0 4v6m-5-7l4.24 4.24m.52.52L16 19M1 12h6m4 0h6M5 5l4.24 4.24m.52.52L14 14"/>
                </svg>
            </button>
        );
    }
    
    return (
        <div
            className={`sidebar-preferences-panel ${className}`}
            style={{
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                width: '320px',
                maxHeight: '400px',
                background: 'rgba(15, 15, 17, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(75, 85, 99, 0.3)',
                borderRadius: '12px',
                padding: '20px',
                zIndex: 100000,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                overflowY: 'auto'
            }}
        >
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <h3 style={{ color: 'white', fontSize: '1.1rem', fontWeight: '600', margin: 0 }}>
                    Sidebar Preferences
                </h3>
                <button
                    onClick={handleToggle}
                    style={{
                        background: 'none',
                        border: 'none',
                        color: 'rgba(255, 255, 255, 0.7)',
                        cursor: 'pointer',
                        padding: '4px'
                    }}
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                </button>
            </div>
            
            {/* Behavior Preferences */}
            <div style={{ marginBottom: '20px' }}>
                <h4 style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', marginBottom: '12px', fontWeight: '500' }}>
                    Behavior
                </h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={userPreferences.autoExpand}
                            onChange={(e) => handlePreferenceChange('autoExpand', e.target.checked)}
                            style={{ accentColor: '#22c55e' }}
                        />
                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                            Auto-expand when selecting items
                        </span>
                    </label>
                    
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={userPreferences.rememberState}
                            onChange={(e) => handlePreferenceChange('rememberState', e.target.checked)}
                            style={{ accentColor: '#22c55e' }}
                        />
                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                            Remember if sidebar is expanded/collapsed when switching pages
                        </span>
                    </label>
                    
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                        <input
                            type="checkbox"
                            checked={userPreferences.defaultOpen}
                            onChange={(e) => handlePreferenceChange('defaultOpen', e.target.checked)}
                            style={{ accentColor: '#22c55e' }}
                        />
                        <span style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.85rem' }}>
                            Open by default on page load
                        </span>
                    </label>
                </div>
            </div>
            
            {/* Cache Management */}
            <div>
                <h4 style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.9rem', marginBottom: '12px', fontWeight: '500' }}>
                    Cache Management
                </h4>
                
                {cacheStats && (
                    <div style={{ 
                        background: 'rgba(255, 255, 255, 0.05)', 
                        padding: '12px', 
                        borderRadius: '8px', 
                        marginBottom: '12px',
                        fontSize: '0.8rem',
                        color: 'rgba(255, 255, 255, 0.7)'
                    }}>
                        <div>Total entries: {cacheStats.total}</div>
                        <div>Valid entries: {cacheStats.valid}</div>
                        <div>Expired entries: {cacheStats.expired}</div>
                        <div>Max size: {cacheStats.maxSize}</div>
                    </div>
                )}
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <button
                        onClick={handleClearCache}
                        style={{
                            background: 'rgba(239, 68, 68, 0.2)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            color: '#f87171',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(239, 68, 68, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                        }}
                    >
                        Clear All Cache
                    </button>
                    
                    <button
                        onClick={() => handleInvalidateCache('company')}
                        style={{
                            background: 'rgba(59, 130, 246, 0.2)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            color: '#60a5fa',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(59, 130, 246, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(59, 130, 246, 0.2)';
                        }}
                    >
                        Clear Company Data
                    </button>
                    
                    <button
                        onClick={() => handleInvalidateCache('news')}
                        style={{
                            background: 'rgba(34, 197, 94, 0.2)',
                            border: '1px solid rgba(34, 197, 94, 0.3)',
                            color: '#4ade80',
                            padding: '8px 12px',
                            borderRadius: '6px',
                            fontSize: '0.8rem',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.target.style.background = 'rgba(34, 197, 94, 0.3)';
                        }}
                        onMouseLeave={(e) => {
                            e.target.style.background = 'rgba(34, 197, 94, 0.2)';
                        }}
                    >
                        Clear News Cache
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SidebarPreferences;
